/**
 * A player no-show is the coach's decision (decisions 154 and 155; docs/booking-contracts.md 13). Read from
 * missResolution with reason "player_no_show", reason before status: pending (the coach decides; it counts by
 * itself at dueAt, 48 hours after the end), counted (by the coach, or "auto"), offered (a single held for the
 * player to book again; a Monthly Coaching lesson back in the month), rebooked. Pure, so the lines are tested.
 * The player's counted and offered lines are UI/UX's (section 13); the waiting line is 154's.
 */
import { firstNameOf } from './liveLessonText.ts';

export interface NoShowBooking {
  status: string;
  membershipId?: string | null;
  coachName?: string;
  playerName?: string;
  missResolution?: { status: string; reason?: string; dueAt?: string; decidedBy?: string } | null;
}

export type PlayerNoShowAction = 'resolve' | 'suggest' | null;

export function isPlayerNoShow(b: NoShowBooking): boolean {
  return b.missResolution?.reason === 'player_no_show';
}

/** Still the coach's or the player's to act on: In Progress for both (140) until then. */
export function playerNoShowOpen(b: NoShowBooking): boolean {
  if (!isPlayerNoShow(b)) return false;
  const s = b.missResolution!.status;
  return s === 'pending' || (s === 'offered' && !b.membershipId);
}

const dayTime = (iso: string, tz: string) =>
  new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz })
    .replace(/, (\d{1,2}:\d{2})/, ' at $1');

export function playerNoShowStatus(b: NoShowBooking, isCoach: boolean, tz: string): {
  line: string; short: string; action: PlayerNoShowAction; note: string | null;
} | null {
  if (!isPlayerNoShow(b)) return null;
  const m = b.missResolution!;
  const player = firstNameOf(b.playerName, 'Your player');
  const coach = firstNameOf(b.coachName, 'Your coach');
  const month = !!b.membershipId;
  const auto = m.decidedBy === 'auto';
  if (m.status === 'pending') {
    return isCoach
      // 155: undecided, it counts by itself 48 hours after the lesson; the page says when.
      ? { line: `${player} didn't join. You decide.`, short: `${player} didn't join. You decide.`, action: 'resolve',
        note: m.dueAt ? `It counts on its own ${dayTime(m.dueAt, tz)} if you don't decide.` : null }
      : { line: `You missed this lesson. ${coach} will decide whether it counts or offer a new time.`, short: `${coach} will decide`, action: null, note: null };
  }
  if (m.status === 'counted') {
    if (isCoach) {
      return auto
        ? { line: `${player} didn't join. It counted on its own after 48 hours.`, short: 'Counted automatically', action: null, note: null }
        : { line: `${player} didn't join. You counted this lesson.`, short: 'Counted', action: null, note: null };
    }
    return month
      ? { line: "You didn't join your lesson, so it counts as one of this month's lessons.", short: 'Lesson credit used', action: null, note: null }
      : { line: `You didn't join your lesson, so it counts as taken. ${coach} was there for it.`, short: 'Charged', action: null, note: null };
  }
  if (m.status === 'offered') {
    if (isCoach) {
      return month
        ? { line: `${player} didn't join. You gave the lesson back to ${player}'s month.`, short: `Back in ${player}'s month`, action: null, note: null }
        : { line: `${player} didn't join. You're holding the lesson; ${player} can pick a new time.`, short: `${player} picking a time`, action: null, note: null };
    }
    return month
      ? { line: `You didn't join your lesson. ${coach} gave it back to your month, so you can book it again.`, short: 'Back in your month', action: null, note: null }
      : { line: `You didn't join your lesson. ${coach} is holding it for you, so pick a new time.`, short: 'Pick a new time', action: 'suggest', note: null };
  }
  if (m.status === 'rebooked') return { line: 'Booked again at no charge.', short: 'Rebooked', action: null, note: null };
  return null;
}
